import SlimSelect from "slim-select";

/**
 * Handle the job search form
 *
 * Manages the selected option pills, the slim-select `select` elements, handlers for filter resets and mobile layouts.
 *
 * @param jobsForm The `form` element
 */
class JobsForm {
  readonly jobsForm: HTMLFormElement;

  // dict of all slim-select elements that are part of the form
  slimSelects: { [elementId: string]: SlimSelect } = {};

  private pillContainer: HTMLDivElement;

  private pillList: HTMLDivElement;

  // we need to prevent that HTMX fires several times during form reset, that's why we block it here
  private isResetting = false;

  public constructor(jobsForm: HTMLFormElement) {
    this.jobsForm = jobsForm;
    this.pillContainer = jobsForm.querySelector<HTMLDivElement>(".ch24-selected-filter-pills");
    this.pillList = this.pillContainer.querySelector<HTMLDivElement>(".pill-list");
  }

  /**
   * Initialize the jobs form
   *
   * Create selects, show the appropriated sub-category, create pills for the selected form fields and add a bunch of
   * event listeners.
   */
  public init(): void {
    this.initSlimSelects();
    this.initSubcategorySwitcher();

    // create initial pills
    this.createPillsForSelectedFields();

    // do not submit the form on enter to avoid page reload
    this.jobsForm.addEventListener("submit", (ev) => { ev.preventDefault() })
    // add event listener and run it once on page load
    this.jobsForm.addEventListener("htmx:beforeRequest", () => this.beforeHtmxRequest());

    // add filter reset listeners to the pill container reset button
    this.pillContainer.querySelector("button.reset-filters").addEventListener("click",
      (ev) => this.resetFilterSelection(ev)
    );

    // add event listener, so we can trigger the reset from within the empty list text
    document.addEventListener("resetFilters", (ev) => this.resetFilterSelection(ev));

    JobsForm.setupMobileFilterToggle();
    JobsForm.initResetFilterButtons();
  }

  /**
   * Set up the toggle button that toggles between results and filter view on mobile devices
   */
  private static setupMobileFilterToggle(): void {
    const toggleFilterButton = document.getElementById("toggleFilters") as HTMLButtonElement;
    const filterAside = document.querySelector<HTMLDivElement>(".filter-aside");
    const resultMain = document.querySelector<HTMLDivElement>("main");
    const footer = document.querySelector<HTMLDivElement>("footer");

    const toggleMobileFilters = () => {
      // Swap button text
      const buttonText = toggleFilterButton.innerText;
      toggleFilterButton.innerText = toggleFilterButton.dataset.altText;
      toggleFilterButton.dataset.altText = buttonText;

      filterAside.classList.toggle("hidden");
      resultMain.classList.toggle("hidden");
      footer.classList.toggle("hidden");
      window.scrollTo(0, 0);
    };

    toggleFilterButton.addEventListener("click", toggleMobileFilters);
    document.querySelector(".show-job-results-button").addEventListener("click", toggleMobileFilters);
  }

  /**
   * Add event listeners to all `.reset-filter-button` buttons that fire the `resetFilter` event.
   */
  private static initResetFilterButtons(): void {
    document.querySelectorAll(".reset-filter-button").forEach((button: HTMLButtonElement) => {
      button.addEventListener("click", (ev) => {
        ev.preventDefault();
        // reset the target inputs value and dispatch keyup event to force HTMX to refresh
        const target = document.getElementById(button.dataset.target) as HTMLInputElement;
        target.value = "";
        target.dispatchEvent(new Event("resetFilter"));
      });
    });
  }

  /**
   * Initialize the subcategory visibility toggler. The category input gets an event listener that switches the
   * visibility of the subcategory inputs.
   */
  private initSubcategorySwitcher(): void {
    const categorySelect = this.jobsForm.querySelector<HTMLSelectElement>("#id_categories");
    const subCategories =
      this.jobsForm.querySelectorAll<HTMLDivElement>("div[data-parent-id='categories']");

    // Toggle sub category visibility based on category selection
    const categoryInputListener = () => {
      const { value } = categorySelect;

      subCategories.forEach((subCategory) => {
        subCategory.classList.toggle("hidden", subCategory.dataset.parentValue !== value);

        if (subCategory.dataset.parentValue !== value) {
          subCategory.querySelectorAll("input.ch24-checkboxes").forEach((checkBox: HTMLInputElement) => {
            checkBox.checked = false;
          });
        }
      });
    };

    // add event listener and run it once on page load
    categorySelect.addEventListener("change", categoryInputListener);
    categoryInputListener();
  }

  /**
   * Create slim selects for all `select` inputs within the form
   */
  private initSlimSelects(): void {
    const isDesktop = window.matchMedia("(min-width: 1000px)").matches
    this.jobsForm.querySelectorAll<HTMLSelectElement>("select").forEach((ele) => {
      this.slimSelects[ele.id] = new SlimSelect({
        select: ele,
        settings: {
          allowDeselect: true,
          showSearch: ele.dataset.showSearch === "true" && isDesktop,
          hideSelected: ele.multiple,
          placeholderText: ele.dataset.placeholder,
        },
        events: {
          searchFilter: (option, search) => {
            return option.text.substring(0, search.length).toLowerCase() === search.toLowerCase()
          }
        }
      });
      ele.addEventListener("htmx:beforeRequest", (ev) => {
        if (this.isResetting) {
          ev.preventDefault();
        }
      });
    });
  }

  /**
   * Create pills for each selected form option and add event listeners for the remove button.
   */
  private createPillsForSelectedFields(): void {
    const formData = new FormData(this.jobsForm);

    // clear pill container
    this.pillList.innerHTML = "";

    const pills: Array<HTMLSpanElement> = [];
    [...formData.entries() as IterableIterator<[string, string]>].forEach(([key, value]: [string, string]) => {
      if (value) {
        const pill = document.createElement("span");
        pill.className = "text-[12px] bg-[--blue-200] py-1 px-3 border border-blue-600 rounded-full";

        const formElements = this.jobsForm
          .querySelectorAll<HTMLInputElement | HTMLSelectElement>(`*[name=${key}]`);
        let formElement: HTMLInputElement | HTMLSelectElement;

        if (formElements.length === 1) {
          [formElement] = formElements;
          if (formElement.tagName === "INPUT") {
            if (formElement.attributes.getNamedItem("type").value === "text") {
              // get value instead of label, because it's a text input
              pill.innerText = formElement.value;
            } else {
              // single input means that we can get the text from the label
              const label = this.jobsForm.querySelector<HTMLLabelElement>(`label[for=${formElement.id}]`);
              pill.innerHTML = label.innerText;
            }
          } else if (formElement.tagName === "SELECT") {
            // slim-select fills the select item with only the selected options with the correct text
            formElement.childNodes.forEach((child: HTMLOptionElement) => {
              if (child.value === value) {
                pill.innerHTML = child.innerText;
              }
            });
          }
        } else {
          // it's a checkbox, so we need to find the element with the correct `name` and `value`
          formElement = [...formElements.values() as IterableIterator<HTMLInputElement>].find(
            (element: HTMLInputElement) => element.value === value
          );
          pill.innerHTML = formElement.labels[0].innerText;
        }

        const pillButton = document.createElement("button");
        pillButton.className = "ml-2 text-brand";
        pillButton.type = "button";
        pillButton.innerHTML = "<svg xmlns=\"http://www.w3.org/2000/svg\" overflow=\"visible\" fill=\"currentColor\" viewBox=\"0 0 102 102\" x=\"0px\" y=\"0px\" width=\"10\" height=\"10\" xml:space=\"preserve\"><g transform=\"translate(1, 1)\"><polygon points=\"89.7,0 50,39.6 10.3,0 0,10.3 39.6,50 0,89.7 10.3,100 50,60.4 89.7,100 100,89.7 60.4,50 100,10.3   \" vector-effect=\"non-scaling-stroke\"></polygon></g></svg>";

        pillButton.addEventListener("click", (ev) => {
          // the default is to reload the page, don't know why
          ev.preventDefault();

          if (formElement.tagName === "INPUT") {
            if (formElement.attributes.getNamedItem("type").value === "checkbox") {
              // checkbox element, simply remove the `checked`
              (formElement as HTMLInputElement).checked = false;
            } else {
              formElement.value = "";
            }
            formElement.dispatchEvent(new Event("resetFilter"));
          } else {
            // remove selection from select via slim-select
            const slimSelect = this.slimSelects[formElement.id];
            slimSelect.setSelected(slimSelect.getSelected().filter(v => v !== value));
          }

          pill.remove();
        });

        pill.appendChild(pillButton);
        pills.push(pill);
      }
    });

    // sort pills alphabetically
    pills.sort((a, b) =>
      // eslint-disable-next-line no-nested-ternary
      a.innerText === b.innerText ? 0 : a.innerText > b.innerText ? 1 : -1
    );

    if (pills.length) {
      // add all pills to the container
      this.pillList.append(...pills);
      this.pillContainer.classList.remove("hidden");
    } else {
      this.pillContainer.classList.add("hidden");
    }
  }

  /**
   * Scroll to top and create new selection pills before the HTMX request is fired
   */
  private beforeHtmxRequest(): void {
    window.scrollTo({ top: 0, left: 0, behavior: "smooth" });
    this.createPillsForSelectedFields();
  }

  /**
   * Reset all form fields
   *
   * Make use of the lock to prevent multiple HTMX requests do to slim select
   *
   * @param ev
   */
  private resetFilterSelection(ev: Event): void {
    ev.preventDefault();

    // start event lock
    this.isResetting = true;

    const formData = new FormData(this.jobsForm);
    [...formData.entries() as IterableIterator<[string, string]>].forEach(([key, value]: [string, string]) => {
      if (value) {
        const formElements = this.jobsForm
          .querySelectorAll<HTMLInputElement | HTMLSelectElement>(`*[name=${key}]`);
        let formElement: HTMLInputElement | HTMLSelectElement;

        if (formElements.length === 1) {
          formElement = formElements[0] as HTMLSelectElement;
        } else {
          // it's a checkbox, so we need to find the element with the correct `name` and `value`
          formElement = [...formElements.values() as IterableIterator<HTMLInputElement>].find(
            (element: HTMLInputElement) => element.value === value
          );
        }

        if (formElement.tagName === "INPUT") {
          if (formElement.attributes.getNamedItem("type").value === "checkbox") {
            // checkbox element, simply remove the `checked`
            (formElement as HTMLInputElement).checked = false;
          } else {
            formElement.value = "";
          }
        } else {
          // remove selection from select via slim-select
          this.slimSelects[formElement.id].setSelected([]);
        }
      }
    });

    // finish event lock
    this.isResetting = false;

    // trigger refresh with categories input
    this.jobsForm.querySelector("input[name='query']").dispatchEvent(new Event("resetFilter"));
  }
}

export default JobsForm;
